/** LOCATION_PERMISSIONS read-only display: the position permissions granted to the
 *  standard roles (broadcast → Tour guide, view → Admin) and whether each is in place.
 *  Run/Preview live on the generic row buttons; this just surfaces the current state.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { PreflightItem } from '@/api/preflight';

interface Grant {
  role: string;
  codename: string;
  role_exists: boolean;
  granted: boolean;
}

export function LocationPermissionsDisplay({ item }: { item: PreflightItem }) {
  const detail = item.detail;
  const grants: Grant[] = Array.isArray(detail.grants)
    ? (detail.grants as unknown[]).map((g) => {
        const o = g as Record<string, unknown>;
        return {
          role: String(o.role ?? ''),
          codename: String(o.codename ?? ''),
          role_exists: o.role_exists === true,
          granted: o.granted === true,
        };
      })
    : [];

  if (!grants.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        No location permission grants defined.
      </Typography>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Position permissions granted to the standard roles:
      </Typography>
      <Stack spacing={0.5}>
        {grants.map((g) => (
          <Stack key={`${g.role}:${g.codename}`} direction="row" spacing={1} alignItems="center">
            {g.granted ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
            )}
            <Typography variant="body2">
              <strong>{g.codename}</strong> → {g.role}
            </Typography>
            {!g.role_exists && (
              <Chip size="small" color="warning" variant="outlined" label="role missing" />
            )}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
